import { useEffect, useMemo } from 'react';
import type { BoundActions, HookActionsFunction, HookFunction, Store } from 'react-sweet-state';
import { createStore, createHook, createActionsHook } from 'react-sweet-state';

import { cfetch } from '@confluence/network';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';

import { formatTapTraits, type TapTraitAttribute } from './formatTapTraits.ts';

export type SiteTraits = {
	attributes: TapTraitAttribute[] | null;
};

export type SiteTraitsState = {
	data: SiteTraits | null;
	loading: boolean;
	error: Error | null;
};

const initialState: SiteTraitsState = {
	data: null,
	loading: false,
	error: null,
};

const actions = {
	start:
		() =>
		({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }): void => {
			setState({ loading: true, error: null });
		},
	success:
		(data: SiteTraits) =>
		({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }): void => {
			setState({ data, loading: false, error: null });
		},
	error:
		(err: Error) =>
		({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }): void => {
			setState({ error: err, loading: false });
		},
	reset:
		() =>
		({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }): void => {
			setState({ data: null, loading: false, error: null });
		},
};

export const siteTraitsStore: Store<
	SiteTraitsState,
	{
		start: () => ({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }) => void;
		success: (
			data: SiteTraits,
		) => ({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }) => void;
		error: (
			err: Error,
		) => ({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }) => void;
		reset: () => ({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }) => void;
	}
> = createStore({
	initialState,
	actions,
	name: 'siteTraitsStore',
});

export const useSiteTraitsStore: HookFunction<
	SiteTraitsState,
	BoundActions<
		SiteTraitsState,
		{
			start: () => ({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }) => void;
			success: (
				data: SiteTraits,
			) => ({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }) => void;
			error: (
				err: Error,
			) => ({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }) => void;
			reset: () => ({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }) => void;
		}
	>,
	void
> = createHook(siteTraitsStore);
export const useSiteTraitsActions: HookActionsFunction<
	BoundActions<
		SiteTraitsState,
		{
			start: () => ({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }) => void;
			success: (
				data: SiteTraits,
			) => ({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }) => void;
			error: (
				err: Error,
			) => ({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }) => void;
			reset: () => ({ setState }: { setState: (s: Partial<SiteTraitsState>) => void }) => void;
		}
	>
> = createActionsHook(siteTraitsStore);

async function fetchSiteTraitsData(cloudId: string): Promise<SiteTraits> {
	const res = await cfetch(`/gateway/api/tap-delivery/api/v3/personalization/site/${cloudId}`, {
		method: 'GET',
	});
	if (!res.ok) {
		const text = await res.text().catch(() => '');
		throw new Error(`Request failed with ${res.status}: ${text || res.statusText}`);
	}
	return (await res.json()) as SiteTraits;
}

// Use a module-level variable to ensure we only fetch once per app lifecycle
// This way multiple components using the hook won't trigger multiple fetches
let hasFetched: boolean = false;

// Test-only utility function to reset the hasFetched flag
export function resetHasFetched(): void {
	hasFetched = false;
}

function getSiteTraitsFromSources(): SiteTraits | null {
	let attributes: TapTraitAttribute[] | null = null;
	// First check window.__SSR_TAP_CONTEXT__ which contains raw tapContext data
	if (typeof window !== 'undefined' && window.__SSR_TAP_CONTEXT__) {
		attributes = formatTapTraits(window.__SSR_TAP_CONTEXT__.site);
		if (attributes) {
			return { attributes };
		}
	}

	// Fallback to getSSRRenderInputs() for SSR scenarios
	const { tapContext } = getSSRRenderInputs();
	attributes = formatTapTraits(tapContext?.site);
	if (attributes) {
		return { attributes };
	}

	return null;
}

export function useSiteTraits({ skipBootstrap }: { skipBootstrap?: boolean } = {}): {
	data: SiteTraits | null;
	loading: boolean;
	error: Error | null;
} {
	const preloadedData = useMemo(() => getSiteTraitsFromSources(), []);

	const [{ data, loading, error }, storeActions] = useSiteTraitsStore();
	const { accessStatus, cloudId } = useSessionData();
	const hasLicensedAccess =
		accessStatus === 'LICENSED_USE_ACCESS' || accessStatus === 'LICENSED_ADMIN_ACCESS';

	useEffect(() => {
		if (preloadedData || data || loading || error) return;

		if (hasFetched || skipBootstrap) return;
		if (!hasLicensedAccess || !cloudId) return;

		hasFetched = true;
		storeActions.start();
		fetchSiteTraitsData(cloudId).then(storeActions.success).catch(storeActions.error);
	}, [
		skipBootstrap,
		preloadedData,
		data,
		loading,
		error,
		storeActions,
		hasLicensedAccess,
		cloudId,
	]);

	if (preloadedData) {
		return { data: preloadedData, loading: false, error: null };
	}

	return { data, loading, error };
}
